import {announce} from '@github-ui/aria-live'
import {Banner, FormControl, TextInput} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {type ChangeEvent, type FormEvent, useCallback, useId, useRef, useState} from 'react'

import type {CreateArtifactDialogProps} from './create-artifact-dialog-types'
import styles from './CreateArtifactDialog.module.css'
import {isSafeFullHref} from './safe-href'
import {useCreateArtifactSubmit} from './use-create-artifact-submit'

const INVALID_LINK_MESSAGE = 'Enter a valid URL that starts with http:// or https://.'

function getLinkValidationError(url: string): string {
  return isSafeFullHref(url) ? '' : INVALID_LINK_MESSAGE
}

export function CreateLinkDialog({resourceId, onClose, onCreated, returnFocusRef}: CreateArtifactDialogProps) {
  const formId = useId()
  const titleInputRef = useRef<HTMLInputElement>(null)
  const [title, setTitle] = useState('')
  const [url, setUrl] = useState('')
  const [urlValidationError, setUrlValidationError] = useState('')
  const {clearCreateErrorMessage, createErrorMessage, isPending, submitArtifact} = useCreateArtifactSubmit({
    resourceId,
    onClose,
    onCreated,
    errorMessage: 'Link could not be added. Try again.',
  })

  const urlIsEmpty = url.trim().length === 0

  const handleTitleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setTitle(event.currentTarget.value)
      clearCreateErrorMessage()
    },
    [clearCreateErrorMessage],
  )

  const handleUrlChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setUrl(event.currentTarget.value)
      setUrlValidationError('')
      clearCreateErrorMessage()
    },
    [clearCreateErrorMessage],
  )

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      if (urlIsEmpty) return

      const trimmedUrl = url.trim()
      const trimmedTitle = title.trim()
      const nextUrlValidationError = getLinkValidationError(trimmedUrl)
      if (nextUrlValidationError) {
        setUrlValidationError(nextUrlValidationError)
        announce(nextUrlValidationError, {assertive: true})
        return
      }

      submitArtifact({
        name: trimmedTitle || trimmedUrl,
        body: trimmedUrl,
        type: 'link',
      })
    },
    [submitArtifact, title, url, urlIsEmpty],
  )

  return (
    <Dialog
      title="Add link"
      className={clsx(styles.CreateDialog, createErrorMessage && styles.CreateDialogHasBanner)}
      width="large"
      height="auto"
      onClose={onClose}
      initialFocusRef={titleInputRef}
      returnFocusRef={returnFocusRef}
      footerButtons={[
        {content: 'Cancel', onClick: onClose, buttonType: 'normal', disabled: isPending},
        {
          content: 'Add link',
          buttonType: 'primary',
          type: 'submit',
          form: formId,
          disabled: urlIsEmpty || isPending,
          loading: isPending,
          loadingAnnouncement: 'Adding link',
        },
      ]}
    >
      <form id={formId} onSubmit={handleSubmit} className={styles.LinkForm} noValidate>
        {createErrorMessage && (
          <Banner title="Unable to add link" variant="critical" description={createErrorMessage} />
        )}
        <FormControl disabled={isPending}>
          <FormControl.Label>Link title</FormControl.Label>
          <TextInput ref={titleInputRef} value={title} block onChange={handleTitleChange} />
        </FormControl>
        <FormControl required disabled={isPending}>
          <FormControl.Label>Link</FormControl.Label>
          <TextInput
            type="url"
            value={url}
            block
            aria-invalid={urlValidationError ? true : undefined}
            onChange={handleUrlChange}
          />
          <FormControl.Caption>Use a full URL, like https://github.com/owner/repo.</FormControl.Caption>
          {urlValidationError && <FormControl.Validation variant="error">{urlValidationError}</FormControl.Validation>}
        </FormControl>
      </form>
    </Dialog>
  )
}
